import React, { Component } from 'react';
import _ from 'lodash';
import FuseboxDiagramEditor from './FuseboxDiagramEditor';
import { IconButton } from '../../common/IconButton';
import LegoAdminPageContext from '../../../pages/legoAdminPageContext';

import { FuseboxTemplateReferences } from './FuseboxTemplateReferences';
import { LinkedImageClusters } from './LinkedImageClusters';
import TemplatedFuseboxDiagram from './TemplatedFuseboxDiagram';
import { parseFuseboxDescriptions } from './fusebox-utils.mjs';
import ClipboardListener, { copyText } from '../../common/clipboard/ClipboardListener';
import ClipboardEnabled from '../../common/clipboard/ClipboardEnabled';
import LegoEditButton from '../LegoEditButton';
import { LocalesList } from '../LocalesList';
import { ModalTranslateFusebox } from './ModalTranslateFusebox';
import FuseLLMSearch from './FuseLLMSearch';
import { FuseboxBoxComparisonPanel } from './FuseboxBoxComparison';

const OPENED_FUSEBOX_URL_PARAM = 'openedFusebox';

class FuseboxLegoEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedFuseboxIndex: 0,
      compareSourceIndex: null,
    };

    // This is a hack used to change the fusebox key and force a rerender when the fusebox object is changed,
    // otherwise after edition+save or parsing fuses the diagram remains outdated.
    this.forcedUpdateId = new Date().valueOf()

    this.nlpService = window.client.service('/services/semantic/nlp');

    // setTimeout(() => this.editFusebox(this.props.value[0], 0), 50);
  }

  componentDidMount() {
    const openedFusebox = this.props.openedFusebox ?? this.context.page.getUrlParam(OPENED_FUSEBOX_URL_PARAM);
    if(openedFusebox !== undefined && this.props.value?.[openedFusebox]) {
      this.editFusebox(this.props.value[openedFusebox], openedFusebox);
    }
  }

  onChange(field, newValue) {
    let copy = { ...this.props.value, [field]: newValue };
    this.props.onChange(copy);
  }

  editFusebox(fusebox, i) {
    this.context.page.setUrlParam(OPENED_FUSEBOX_URL_PARAM, i);

    let editorRef = React.createRef();
    this.context.page.openModal(<FuseboxDiagramEditor ref={editorRef} lego={this.props.lego} fusebox={fusebox} onSave={newFusebox => {
      this.props.value[i] = newFusebox;
      this.forcedUpdateId = new Date().valueOf();
      this.props.onChange(this.props.value);
      this.context.page.deleteUrlParam(OPENED_FUSEBOX_URL_PARAM);
      this.context.page.closeModal();
    }}/>, {
      title: `Box ${i+1} of ${this.props.lego.type} ${this.props.lego._id}`,
      onClose: () => this.context.page.deleteUrlParam(OPENED_FUSEBOX_URL_PARAM),
      fullScreen: true,
      closeConfirmationCbk: () => {
        // Only ask for confirmation if the editor has unsaved changes
        if(editorRef.current && _.isEqual(editorRef.current.state.changedFusebox, fusebox)) {
          return true;
        }
        return confirm('Are you sure you wanna close it? You will lose all your changes!');
      }
    });
  }

  removeFusebox(fusebox) {
    if (confirm('Are you really sure you want to DELETE it?')) {
      this.props.onChange(_.without(this.props.value, fusebox));
    }
  }

  copyFusebox(fusebox) {
    copyText(JSON.stringify(fusebox)).catch(console.error);
  }

  handleClipboardChange(clipboard) {
    if(_.isString(clipboard) && clipboard) {
      try {
        let fbox = JSON.parse(clipboard);
        if(fbox && fbox.fuses && fbox.boxAspectRatio) {
          this.setState({clipboardFusebox: fbox});
          return;
        }
      } catch (err) {

      }
    }
    this.setState({clipboardFusebox: null})
  }

  addFusebox() {
    let fuses = [...(this.props.value || []), { fuses: [], boxAspectRatio: 0.5 }];
    this.props.onChange(fuses);
  }

  pasteFusebox()  {
    let fuses = [...(this.props.value || []), this.state.clipboardFusebox];
    this.props.onChange(fuses);
  }

  toggleFuseboxVisibility(fusebox) {
    if(fusebox.hidden) {
      delete fusebox.hidden;
    } else {
      fusebox.hidden = true;
    }
    this.props.onChange(this.props.value);
  }

  async parseFuseboxes(forceLLM = false) {
    await parseFuseboxDescriptions(this.props.value, this.nlpService, this.props.lego.lang, forceLLM);
    this.forcedUpdateId = new Date().valueOf();
    this.props.onChange(this.props.value);
  }

  openTranslateDialog() {
    let translationDone = false;
    this.context.page.openModal(<ModalTranslateFusebox lego={this.props.lego} onTranslationDone={() => {
      translationDone = true;
    }}/>, {
      title: 'Translate fusebox ' + this.props.lego._id,
      onClose: () => {
        if (translationDone) {
          this.props.onReload();
        }
      }
    });
  }

  updateTranslation() {
    this.context.page.runAsync(async () => {
      let changedLego = await this.context.page.service('services/data/fusebox-translation').update(this.props.lego._id, {save: false});
      this.props.onChange(changedLego.data);
    }, 'Updating translation...');
  }

  openLLMSearch() {
    this.context.page.openModal(
      <FuseLLMSearch boxes={this.props.value || []} />,
      { title: 'LLM Fuse Search', minSize: true }
    );
  }

  openLinkedImageClusters() {
    this.context.page.openModal(
      <LinkedImageClusters templateId={this.props.lego._id}
                           templateContext={this.props.lego.context}
                           templateClusters={this.props.value?.[0]?.imageClusters}/>,
      { title: `Image clusters linked via template ${this.props.lego._id}`, minSize: true }
    );
  }

  boxComparisonLabel(fusebox, index) {
    return fusebox?.boxName ? `Box ${index + 1}: ${fusebox.boxName}` : `Box ${index + 1}`;
  }

  toggleCompareMode(index) {
    this.setState(state => ({
      compareSourceIndex: state.compareSourceIndex === index ? null : index,
    }));
  }

  openBoxComparison(sourceIndex, targetIndex) {
    const boxA = this.props.value[sourceIndex];
    const boxB = this.props.value[targetIndex];

    this.context.page.openModal(
      <FuseboxBoxComparisonPanel boxA={boxA}
                                 boxB={boxB}
                                 labelA={this.boxComparisonLabel(boxA, sourceIndex)}
                                 labelB={this.boxComparisonLabel(boxB, targetIndex)}/>,
      {
        title: `Compare ${this.boxComparisonLabel(boxA, sourceIndex)} vs ${this.boxComparisonLabel(boxB, targetIndex)}`,
        minSize: true,
      }
    );
    this.setState({ compareSourceIndex: null });
  }

  render() {
    const {isTemplate, value} = this.props;

    const {clipboardFusebox, compareSourceIndex} = this.state;

    let warning = null;
    if (isTemplate) {
      if (value?.length !== 1) {
        warning = <div className={'alert alert-danger'}>A fuseboxTemplate MUST HAVE exactly ONE ONLY fusebox. FIX IT or change the type to 'fusebox'</div>
      }
    }

    const swap = (from, to) => {
      let data = [... this.props.value];
      data[from] = this.props.value[to];
      data[to] = this.props.value[from];
      this.props.onChange(data);
    };

    let translateButton, updateTranslationButton;

    if(!this.props.changed && !this.props.lego.labels?.includes('readonly')) {
      translateButton =  <IconButton icon={'translate'}
                                     onClick={() => this.openTranslateDialog()}
                                     className={'mt-2'}>Translate...</IconButton>
    }

    if(_.find(this.props.lego.labels, l => l.startsWith('translatedFrom:'))) {
      updateTranslationButton =  <IconButton level={'violeta'} icon={'translate'}
                                             onClick={() => this.updateTranslation()}
                                             className={'mt-2'}>Update <LocalesList locales={this.props.lego.locales}/> translation</IconButton>
    }

    return <div className={''}>
      <ClipboardListener onChange={val => this.handleClipboardChange(val)}/>

      {warning}
      <div className={'row no-gutters justify-content-around'}>
        {
          _.flatten(_.map(value || [], (fusebox, i) => {
              const hiddenClass = fusebox.hidden ? ' translucent' : '';
              const progressBackground = fusebox.fuses?.length ? (isTemplate ? 'bg-gray-blue' : 'bg-dark') : 'bg-warning';

              let diagramOfImage;
              if (fusebox.fuses?.length) {
                diagramOfImage = <TemplatedFuseboxDiagram fitWidth={300} development={true} fusebox={fusebox} clipTitle={true}/>;
              } else if(fusebox.boxDiagramImg) {
                diagramOfImage = <img className={'rounded'} style={{ maxHeight: '100%', maxWidth: '100%' }}
                                      src={fusebox.boxDiagramImg?.url}/>
              } else {
                diagramOfImage = <div>
                  <div>{fusebox.boxName}</div>
                  <img className={'rounded'} style={{ maxHeight: '180px', maxWidth: '200px' }}
                       src={fusebox.boxTablesImages?.[0]?.url}/>
                </div>
              }
              let key = fusebox.boxName + this.forcedUpdateId;
              // In weird cases, there are two boxes with the same box name. Add index to tell those boxes apart
              if(_.filter(value, box => box.boxName === fusebox.boxName)) {
                key += i;
              }

              const isCompareSource = compareSourceIndex === i;
              const isCompareTarget = compareSourceIndex !== null && compareSourceIndex !== i;

              return [
                <div className={`${isTemplate ? 'bg-gris-blue' : 'bg-dark'} mb-1 mr-1${hiddenClass}`}
                     key={key}
                     style={{ display: 'inline-block' }}>

                  <div className={`${progressBackground}`} style={{ height: '40vh', maxWidth: '25vw', minWidth: isTemplate ? '300px' : '150px' }}>
                    {diagramOfImage}
                  </div>

                  <div className={'text-center'}>
                    <IconButton level={'primary'} icon={'edit'} onClick={() => this.editFusebox(fusebox, i)}/>
                    <IconButton level={'danger'} icon={'delete'} onClick={() => this.removeFusebox(fusebox)}/>
                    <IconButton level={fusebox.hidden ? 'danger' : 'success'}
                                icon={fusebox.hidden ? 'visibility_off' : 'visibility'}
                                onClick={() => this.toggleFuseboxVisibility(fusebox)}
                    />
                    <ClipboardEnabled>
                      <IconButton icon={'content_copy'} onClick={() => this.copyFusebox(fusebox, i)}/>
                    </ClipboardEnabled>
                    
                    { !isCompareTarget || isCompareSource ? <IconButton level={isCompareSource ? 'warning' : 'secondary'}
                                icon={'compare'}
                                description={isCompareSource ? 'Exit compare mode' : 'Compare this box'}
                                onClick={() => this.toggleCompareMode(i)}/>
                    : null }

                    { isCompareTarget ?
                      <IconButton level={'warning'}
                                  icon={'gps_fixed'}
                                  description={'Compare against selected box'}
                                  onClick={() => this.openBoxComparison(compareSourceIndex, i)}/>
                      : null }
                  </div>

                  {
                    fusebox.templateId ?
                      <div className={'small text-center pb-1'}>
                        <LegoEditButton icon={'fingerprint'} legoId={fusebox.templateId} extraQuery={`${OPENED_FUSEBOX_URL_PARAM}=0`} editorProps={{
                          onAfterSave: (updated) => {
                            this.forcedUpdateId = new Date().valueOf();
                            this.forceUpdate();
                          }
                        }}>
                          <span className={'small'}>{fusebox.templateId}</span>
                        </LegoEditButton>
                      </div>
                      :
                      null
                  }
                </div>,
                (i+1) < value.length ? <div key={'controls-' + i} className={'flex-grow-0 align-self-center'}>
                  <div><IconButton icon={'swap_horiz'} className={'p-0'} onClick={() => swap(i, i + 1)}/></div>
                </div> : null
              ];
            }
          ))
        }

        <div className={'d-flex flex-column'}>
          {
            (!isTemplate || !value?.length) ?
              <IconButton className={'m-3'} icon={'note_add'} level={'success'} onClick={() => this.addFusebox()}> Add
                diagram</IconButton>
              : null
          }

          { clipboardFusebox ?
            <IconButton className={'m-3'} icon={'content_paste'} level={'success'} onClick={() => this.pasteFusebox()}>
              Paste box
            </IconButton> : null }

          {
            isTemplate && this.props.lego._id ? <div>
              <div className={'h6'}>Fuseboxes using this template:</div>
              <FuseboxTemplateReferences templateId={this.props.lego._id}/>
            </div> : null
          }

          <span className='mt-2'>
            <IconButton icon={'segment'}
              onClick={() => this.context.page.runAsync(this.parseFuseboxes(), 'Parsing fuseboxes...')}>
              Parse
            </IconButton>

            <IconButton icon={'auto_awesome'} level={'violeta'}
              onClick={() => this.context.page.runAsync(this.parseFuseboxes(true), 'Parsing fuseboxes with LLM...')}
              className={''}>
              LLM Parse
            </IconButton>

            { isTemplate ? <IconButton icon={'collections'} level={'secondary'} onClick={() => this.openLinkedImageClusters()}>       
                Linked image clusters
              </IconButton>
              : null }
          </span>


          { translateButton }

          { updateTranslationButton }

          <IconButton icon={'search'} level={'secondary'}
            onClick={() => this.openLLMSearch()}
            className={'mt-2'}>
            LLM Fuse Search
          </IconButton>
        </div>
      </div>
    </div>;
  }
}

FuseboxLegoEditor.contextType = LegoAdminPageContext;

export default FuseboxLegoEditor;
// //TODO: Hack to have vite hot reloading working
// export default function ComponentWrapper(props) {
//   return <FuseboxLegoEditor {...props}/>;
// }
